import { Container, Box, Skeleton, Card, Grid, Stack } from '@castlery/fortress';

/**
 * 产品卡片骨架屏组件
 * 匹配 ProductCard 的真实结构
 */
export function ProductCardSkeleton() {
  return (
    <Card
      variant="plain"
      sx={{
        bgcolor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        '--Card-padding': 0,
      }}
    >
      {/* 产品图片 - 1:1 宽高比 */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingBottom: '100%', // 创建1:1宽高比
          overflow: 'hidden',
          borderRadius: 'sm',
        }}
      >
        <Skeleton
          variant="overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'sm',
          }}
        >
          <img alt="" src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" />
        </Skeleton>
      </Box>

      {/* 产品信息 */}
      <Box sx={{ mt: 1.5 }}>
        {/* 产品名称 */}
        <Skeleton variant="text" sx={{ mb: 0.5 }} />

        {/* 产品描述 */}
        <Skeleton variant="text" width="70%" sx={{ mb: 1 }} />

        {/* 价格 */}
        <Skeleton variant="text" width="35%" />

        {/* 颜色选项 */}
        <Box sx={{ mt: 1, display: 'flex', gap: 0.75 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="circular" width={24} height={24} />
          ))}
        </Box>
      </Box>
    </Card>
  );
}

export interface SearchLoadingSkeletonProps {
  /**
   * 是否显示面包屑骨架屏
   * @default true
   */
  showBreadcrumbs?: boolean;
  /**
   * 是否显示横幅骨架屏
   * @default true
   */
  showBanner?: boolean;
  /**
   * 横幅高度配置
   * @default { xs: 146, md: 252 }
   */
  bannerHeight?: { xs: number; md: number };
  /**
   * 产品网格列数配置
   * @default { xs: 6, sm: 4, lg: 3 }
   */
  gridColumns?: { xs: number; sm?: number; lg: number };
  /**
   * 每页显示的产品数量（用于生成骨架屏数量）
   * @default 12
   */
  productsPerPage?: number;
}

/**
 * 搜索页面加载骨架屏
 * 完全匹配真实页面的布局和样式
 *
 * 适用于：
 * - Product Listing Page (PLP)
 * - Category Landing Page
 * - Search Results Page
 *
 * @example
 * ```tsx
 * // 基础用法
 * <SearchLoadingSkeleton />
 *
 * // 自定义配置
 * <SearchLoadingSkeleton
 *   showBanner={false}
 *   productsPerPage={24}
 * />
 * ```
 */
export function SearchLoadingSkeleton({
  showBreadcrumbs = true,
  showBanner = true,
  bannerHeight = { xs: 146, md: 252 },
  gridColumns = { xs: 6, sm: 4, lg: 3 },
  productsPerPage = 12,
}: SearchLoadingSkeletonProps = {}) {
  return (
    <Container
      disableGutters
      sx={{
        scrollBehavior: 'auto',
        '& *': {
          scrollBehavior: 'auto',
        },
      }}
    >
      {/* 面包屑 Skeleton */}
      {showBreadcrumbs && (
        <Container>
          <Box sx={{ py: { xs: 1.5, md: 2 } }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Skeleton variant="text" width={50} />
              <Skeleton variant="text" width={8} />
              <Skeleton variant="text" width={100} />
            </Stack>
          </Box>
        </Container>
      )}

      {/* 横幅图片 Skeleton */}
      {showBanner && (
        <Container disableGutters>
          <Card
            sx={{
              height: bannerHeight,
              border: 'none',
              boxShadow: 'none',
              '--Card-padding': 0,
            }}
          >
            <Skeleton
              variant="overlay"
              sx={{
                position: 'absolute',
                inset: 0,
              }}
            >
              <img alt="" src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" />
            </Skeleton>
          </Card>
        </Container>
      )}

      {/* 搜索结果区域 */}
      <Container sx={{ mt: { xs: 3, md: 4 }, mb: 8 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* 左侧过滤器 Skeleton - 仅桌面端显示 */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: 280,
              flexShrink: 0,
            }}
          >
            <Stack spacing={2.5}>
              {/* 过滤器标题 */}
              <Skeleton variant="text" width="40%" />

              {/* 过滤器选项组 */}
              {[1, 2, 3, 4].map((i) => (
                <Box key={i}>
                  <Skeleton variant="text" width="50%" sx={{ mb: 1 }} />
                  <Stack spacing={0.75}>
                    <Skeleton variant="rectangular" height={24} width="85%" sx={{ borderRadius: 'sm' }} />
                    <Skeleton variant="rectangular" height={24} width="70%" sx={{ borderRadius: 'sm' }} />
                    <Skeleton variant="rectangular" height={24} width="75%" sx={{ borderRadius: 'sm' }} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* 右侧产品区域 */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* 移动端过滤器和排序按钮 */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: 2,
                mb: 3,
              }}
            >
              <Skeleton variant="rectangular" height={40} sx={{ flex: 1, borderRadius: 'sm' }} />
              <Skeleton variant="rectangular" height={40} sx={{ flex: 1, borderRadius: 'sm' }} />
            </Box>

            {/* 桌面端产品数量和排序 */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Skeleton variant="text" width={180} />
              <Skeleton variant="rectangular" width={220} height={40} sx={{ borderRadius: 'sm' }} />
            </Box>

            {/* 产品卡片网格 - 使用真实的Grid布局 */}
            <Grid container m={0} columnSpacing={4} rowSpacing={{ xs: 4, md: 6 }}>
              {Array.from({ length: productsPerPage }).map((_, index) => (
                <Grid key={index} {...gridColumns}>
                  <ProductCardSkeleton />
                </Grid>
              ))}
            </Grid>

            {/* 分页 Skeleton */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 6,
              }}
            >
              <Stack direction="row" spacing={1}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" width={40} height={40} sx={{ borderRadius: 'sm' }} />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Container>
  );
}
